/* gg-header.js — Transparent header floating over the hero (Roadmap #1).
   The homepage <body> carries `gg-hero-header` (set in theme.liquid). While the
   hero fills the viewport the header stays transparent with a light logo/nav;
   once the user scrolls past the hero we add `gg-scrolled` and the CSS settles
   the header into a solid warm bar. Safe no-op on any page without a video hero. */
(function () {
  'use strict';

  function init() {
    var body = document.body;
    if (!body || !body.classList.contains('gg-hero-header')) return;

    // Safety: if there is no video hero at the top, fall back to the normal header.
    var hero = document.querySelector('[id^="VideoHero-"]');
    if (!hero) {
      body.classList.remove('gg-hero-header');
      return;
    }

    var headerWrap = document.querySelector('.header-wrapper');
    var isSolid = false;

    function update() {
      var headerH = headerWrap ? headerWrap.offsetHeight : 100;
      // Flip to solid just before the hero scrolls fully out of view.
      var threshold = Math.max(hero.offsetHeight - headerH, 60);
      var shouldBeSolid = window.scrollY > threshold;
      if (shouldBeSolid !== isSolid) {
        isSolid = shouldBeSolid;
        body.classList.toggle('gg-scrolled', isSolid);
      }
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  // Play product-card videos on hover (video-on-hover, roadmap #2)
  function initCardVideos() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var vids = document.querySelectorAll('.gg-card-video');
    for (var i = 0; i < vids.length; i++) {
      (function (v) {
        var card = v.closest('.card-wrapper') || v.closest('.card') || v.parentElement;
        if (!card || card.dataset.ggVid) return;
        card.dataset.ggVid = '1';
        card.addEventListener('mouseenter', function () {
          if (v.preload === 'none') v.preload = 'auto';
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        });
        card.addEventListener('mouseleave', function () {
          v.pause();
          try { v.currentTime = 0; } catch (e) {}
        });
      })(vids[i]);
    }
  }

  // Scroll-in reveal (roadmap #3): fade + rise sections as they enter the viewport
  function initReveal() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    var main = document.getElementById('MainContent');
    if (!main) return;
    var sections = main.querySelectorAll(':scope > .shopify-section');
    var targets = [];
    for (var i = 0; i < sections.length; i++) {
      if (i === 0) continue; // leave the hero (above the fold, has its own intro)
      sections[i].classList.add('gg-reveal');
      targets.push(sections[i]);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('gg-is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  // Wishlist hearts: the Swym/Wishlist Plus proxy that used to live here is
  // gone. It existed only to mirror clicks into the app's own control and read
  // its "added" class back out. The wishlist is now ours — see
  // assets/gg-wishlist.js, which owns the hearts, the storage and the count.

  // The homepage header is fixed at top: 0. If an announcement bar is present
  // it would be hidden underneath, so publish its height as a CSS variable and
  // let the header offset itself. Re-measured on resize (the bar wraps to two
  // lines on narrow screens).
  function measureAnnouncementBar() {
    var bar = document.querySelector('.announcement-bar, .utility-bar');
    var h = bar && bar.offsetHeight ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--gg-announcement-h', h + 'px');
  }

  // Force the open animations to replay.
  //
  // Relying on the browser to restart a CSS animation when <details> reopens is
  // not dependable — with animation-fill-mode: both the animation is already
  // finished, and the element was never removed from the DOM, so it can stay
  // frozen at its end state. Every animation is scoped to `.gg-anim`; removing
  // the class, forcing a reflow, then re-adding it guarantees a fresh run.
  function restartAnim(details) {
    details.classList.remove('gg-anim');
    void details.offsetWidth;          // reflow — do not remove
    details.classList.add('gg-anim');
  }

  // Open the header dropdowns on hover.
  //
  // Crave builds these as <details>/<summary>, which only respond to click.
  // CSS cannot do this — a browser hides a <details>' non-summary children
  // itself, so the `open` attribute has to be toggled in JS.
  //
  // Pointer devices only: on touch there is no hover, and forcing `open` there
  // would make the first tap open a menu the user cannot dismiss.
  function initMenuHover() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;

    var menus = document.querySelectorAll('.header__inline-menu > ul > li > header-menu > details');

    // Close every other panel instantly. Without this, moving from one nav item
    // to the next leaves the previous panel open for the 180ms grace period, so
    // two panels are on screen at once and visibly overlap.
    function closeOthers(keep) {
      for (var j = 0; j < menus.length; j++) {
        if (menus[j] !== keep) {
          menus[j].open = false;
          menus[j].classList.remove('gg-anim');
        }
      }
    }

    for (var i = 0; i < menus.length; i++) {
      (function (details) {
        var closeTimer = null;
        var parent = details.parentElement;   // <header-menu>

        function open() {
          clearTimeout(closeTimer);
          closeOthers(details);
          details.open = true;
          restartAnim(details);
        }
        function close() {
          // Small grace period so moving the cursor from the label down into
          // the panel does not close it in the gap between them.
          closeTimer = setTimeout(function () {
            details.open = false;
            details.classList.remove('gg-anim');
          }, 180);
        }

        parent.addEventListener('mouseenter', open);
        parent.addEventListener('mouseleave', close);
        // Keyboard users still get click-to-toggle; do not fight it.
        details.addEventListener('focusout', function (e) {
          if (!details.contains(e.relatedTarget)) close();
        });
      })(menus[i]);
    }

    // Level 3 — the flyout panels. Same hover behaviour, scoped to the siblings
    // within one panel so opening one closes the others in that panel only.
    var panels = document.querySelectorAll('.header__inline-menu .header__submenu');
    for (var p = 0; p < panels.length; p++) {
      (function (panel) {
        var subs = panel.querySelectorAll(':scope > li > details');
        for (var s = 0; s < subs.length; s++) {
          (function (details) {
            var li = details.parentElement;
            var timer = null;
            li.addEventListener('mouseenter', function () {
              clearTimeout(timer);
              for (var k = 0; k < subs.length; k++) {
                if (subs[k] !== details) {
                  subs[k].open = false;
                  subs[k].classList.remove('gg-anim');
                }
              }
              details.open = true;
              restartAnim(details);
            });
            li.addEventListener('mouseleave', function () {
              timer = setTimeout(function () {
                details.open = false;
                details.classList.remove('gg-anim');
              }, 180);
            });
          })(subs[s]);
        }
      })(panels[p]);
    }
  }

  // runDedupe() existed to sweep up Swym's asynchronously-injected duplicate
  // hearts. Our own hearts are rendered by Liquid and bound by delegation, so
  // there is nothing to sweep. Kept as a no-op rather than unpicking every call
  // site, which would risk breaking the unrelated init sequence below.
  function runDedupe() {}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initCardVideos(); initReveal(); runDedupe(); initMenuHover(); measureAnnouncementBar(); });
  } else {
    init();
    initCardVideos();
    initReveal();
    runDedupe();
    initMenuHover();
    measureAnnouncementBar();
  }
  window.addEventListener('resize', measureAnnouncementBar, { passive: true });
})();
